/**
 * Sign-in and sign-up against the engine.
 *
 * Extracted so the v1 login gate and the v2 auth screen cannot drift apart on the
 * request shape, the cookie mode, or how a FastAPI validation error is unwrapped.
 * Both paths save the session on success; the caller decides where to go next.
 */
import { ENGINE_BASE } from "./config";
import { saveWebSession, type WebSession } from "./authSession";

export type SessionResponse = {
  token: string;
  expires_at?: string;
  user: { email: string; display_name?: string | null };
  organization: { slug: string; name?: string };
};

export type RegisterInput = {
  email: string;
  password: string;
  organizationName: string;
  displayName?: string;
  /** The KVKK/terms box. The engine answers 400 consent_required unless true. */
  consent: boolean;
};

/** Stored with the user by the engine. Bump when the KVKK/terms text changes. */
export const SIGNUP_CONSENT_VERSION = "terms-2026-10";

/**
 * FastAPI returns a string detail, a list of validation objects, or (org
 * subdomain refusals) a `{code, message}` object.
 */
export function parseErrorDetail(body: unknown, fallback: string): string {
  const detail = (body as { detail?: unknown })?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0] && typeof detail[0] === "object") {
    return (detail[0] as { msg?: string }).msg || fallback;
  }
  if (detail && typeof detail === "object") {
    const message = (detail as { message?: unknown }).message;
    if (typeof message === "string" && message) return message;
  }
  return fallback;
}

/** Machine code of a `{detail: {code}}` error body, if any (`org_mismatch`, `unknown_organization`). */
export function parseErrorCode(body: unknown): string | undefined {
  const detail = (body as { detail?: unknown })?.detail;
  if (detail && typeof detail === "object" && !Array.isArray(detail)) {
    const code = (detail as { code?: unknown }).code;
    if (typeof code === "string" && code) return code;
  }
  return undefined;
}

/** A refused sign-in / sign-up; `code` lets the auth page word engine refusals. */
export class AuthRequestError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code?: string,
  ) {
    super(message);
    this.name = "AuthRequestError";
  }
}

export function sessionFromResponse(data: SessionResponse): WebSession {
  return {
    token: data.token,
    email: data.user.email,
    displayName: data.user.display_name,
    organizationSlug: data.organization.slug,
    organizationName: data.organization.name,
    expiresAt: data.expires_at,
  };
}

async function post(path: string, body: unknown, failure: string): Promise<WebSession> {
  const res = await fetch(`${ENGINE_BASE}${path}`, {
    method: "POST",
    // The engine sets an HttpOnly session cookie alongside the bearer token.
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const detail = await res.json().catch(() => ({}));
    throw new AuthRequestError(
      parseErrorDetail(detail, `${failure} (${res.status})`),
      res.status,
      parseErrorCode(detail),
    );
  }
  const session = sessionFromResponse((await res.json()) as SessionResponse);
  saveWebSession(session);
  return session;
}

export function login(email: string, password: string): Promise<WebSession> {
  return post(
    "/v1/auth/login",
    { email: email.trim(), password },
    "Sign in failed",
  );
}

export function register(input: RegisterInput): Promise<WebSession> {
  return post(
    "/v1/auth/register",
    {
      email: input.email.trim(),
      password: input.password,
      organization_name: input.organizationName.trim(),
      display_name: input.displayName?.trim() || undefined,
      consent: { accepted: input.consent, document_version: SIGNUP_CONSENT_VERSION },
    },
    "Sign up failed",
  );
}
